import { useState, useRef, useEffect } from "react";
import { HiCheckCircle, HiRefresh, HiChevronDown } from "react-icons/hi";
import {
  HiExclamationTriangle,
  HiNoSymbol,
  HiPencilSquare,
} from "react-icons/hi2";
import { FaRegCircle } from "react-icons/fa";

const ACTION_STATUSES = [
  {
    value: "pending",
    label: "Pending",
    Icon: FaRegCircle,
    color: "text-gray-500",
    borderColor: "border-gray-300",
  },
  {
    value: "in_progress",
    label: "In Progress",
    Icon: HiRefresh,
    color: "text-blue-600",
    borderColor: "border-blue-300",
  },
  {
    value: "completed",
    label: "Completed",
    Icon: HiCheckCircle,
    color: "text-emerald-600",
    borderColor: "border-emerald-300",
  },
  {
    value: "blocked",
    label: "Blocked",
    Icon: HiExclamationTriangle,
    color: "text-amber-600",
    borderColor: "border-amber-300",
  },
  {
    value: "wont_do",
    label: "Won't Do",
    Icon: HiNoSymbol,
    color: "text-red-500",
    borderColor: "border-red-300",
  },
];

const StatusDropdown = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current =
    ACTION_STATUSES.find((s) => s.value === status) ?? ACTION_STATUSES[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-md border bg-white px-2 py-1 text-[11px] font-semibold transition-colors hover:bg-gray-50 ${current.color} ${current.borderColor}`}
      >
        <current.Icon className="size-3" />
        {current.label}
        <HiChevronDown className="size-2.5 opacity-50" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {ACTION_STATUSES.map(({ value, label, Icon, color }) => (
            <button
              key={value}
              onClick={() => {
                onChange(value);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-gray-50 ${color} ${
                value === status ? "font-semibold" : ""
              }`}
            >
              <Icon className="size-3" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ActionItem = ({ action, onStatusChange, onNoteChange }) => {
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState(action.note || "");
  const isCompleted = action.status === "completed";
  const isWontDo = action.status === "wont_do";
  const isDimmed = isCompleted || isWontDo;

  const handleSaveNote = () => {
    onNoteChange(action.id, noteValue.trim() || null);
    setEditingNote(false);
  };

  return (
    <div className="rounded-md border border-gray-100 px-2.5 py-2">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">
          <StatusDropdown
            status={action.status}
            onChange={(v) => onStatusChange(action.id, v)}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span
              className={`flex-1 truncate text-sm leading-relaxed ${
                isDimmed ? "text-gray-400 line-through" : "text-gray-900"
              }`}
            >
              {action.title}
            </span>
            {!editingNote && (
              <button
                onClick={() => setEditingNote(true)}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[10px] text-blue-600 hover:underline"
              >
                <HiPencilSquare className="size-3" />
                {action.note ? "Edit note" : "Add note"}
              </button>
            )}
          </div>
          {editingNote ? (
            <div className="mt-1.5 space-y-1.5">
              <textarea
                className="w-full resize-none rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px] text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                placeholder="Add a note..."
                autoFocus
              />
              <div className="flex gap-1.5">
                <button
                  onClick={handleSaveNote}
                  className="rounded bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setNoteValue(action.note || "");
                    setEditingNote(false);
                  }}
                  className="rounded px-2 py-0.5 text-[10px] font-medium text-gray-500 hover:bg-gray-100"
                >
                  Cancel
                </button>
                {action.note && (
                  <button
                    onClick={() => {
                      onNoteChange(action.id, null);
                      setNoteValue("");
                      setEditingNote(false);
                    }}
                    className="ml-auto rounded px-2 py-0.5 text-[10px] font-medium text-red-500 hover:bg-red-50"
                  >
                    Delete note
                  </button>
                )}
              </div>
            </div>
          ) : (
            action.note && (
              <div className="mt-1">
                <textarea
                  className="w-full cursor-default resize-none rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600 focus:outline-none"
                  rows={1}
                  value={action.note}
                  readOnly
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
